/**
 * BrandSection Component
 * 로그인 페이지 좌측 브랜드 섹션
 *
 * @description
 * auth 페이지의 좌측에 표시되는 브랜드 정보 영역
 * VROONG 로고, 제목, 설명, 특징 목록을 표시합니다.
 */
import React, { memo } from "react";
import { VroongLogo } from "@/shared/components/icons/VroongLogo";

// ============================================================
// 타입 정의
// ============================================================

/** Feature 항목 인터페이스 */
export interface Feature {
    icon: React.ReactNode;
    title: string;
    description: string;
}

/** BrandSection props */
interface BrandSectionProps {
    /** 브랜드 섹션 제목 */
    title: string;
    /** 브랜드 섹션 설명 */
    description: string | React.ReactNode;
    /** 특징 목록 */
    features: Feature[];
}

// ============================================================
// 컴포넌트 구현
// ============================================================

/**
 * 브랜드 섹션 컴포넌트
 * @param props - BrandSectionProps
 */
export const BrandSection: React.FC<BrandSectionProps> = ({
    title,
    description,
    features
}) => (
    <div className="brand-section">
        <div className="brand-content">
            {/* VROONG 로고 */}
            <div className="logo-container">
                <VroongLogo />
            </div>

            {/* 제목 및 설명 */}
            <h1 className="brand-title">{title}</h1>
            <p className="brand-description">{description}</p>

            {/* 브랜드 특징 목록 */}
            <div className="features">
                {features.map((feature, index) => (
                    <div key={index} className="feature-item">
                        <div className="feature-icon">{feature.icon}</div>
                        <div className="feature-text">
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* 브랜드 그래픽 요소 */}
            <div className="brand-graphics">
                <div className="geometric-shape triangle-1"></div>
                <div className="geometric-shape triangle-2"></div>
                <div className="geometric-shape hexagon"></div>
                <div className="line-pattern"></div>
                <div className="gradient-overlay"></div>
            </div>
        </div>
    </div>
);

// React.memo 사용하여 불필요한 리렌더링 방지
export default memo(BrandSection);
